import { OutcomeItem, IncomeItem, BankStatementMovement, incomeReceiptLiquido, outcomeParcelValorExibido } from "@/services/financeiro";

/* ─── Types de saída ─────────────────────────────────────────────────────── */

export interface MesStat {
  mes:          string;
  mesKey:       string;
  receitas:     number;
  despesas:     number;
  saldo:        number;
  outcomeItems: OutcomeItem[];
  incomeItems:  IncomeItem[];
  /** Movimentos de extrato bancário (SEMOG) somados ao mês */
  statementItems: BankStatementMovement[];
}

export interface SaldoAcumuladoStat {
  mes:          string;
  mesKey:       string;
  receitas:     number;
  despesas:     number;
  saldo:        number;
  saldoAcum:    number;
}

export interface StatusPagStat {
  name:       string;
  value:      number;
  quantidade: number;
  fill:       string;
  items:      OutcomeItem[];
}

export interface CentroCustoFinStat {
  name:       string;
  valor:      number;
  quantidade: number;
  items:      OutcomeItem[];
  /** % do total pago no período (todos os CC, não só o Top exibido). */
  pct?:       number;
}

export interface CentroCustoRecebimentoStat {
  name:       string;
  valor:      number;
  quantidade: number;
  items:      IncomeItem[];
  /** % do total recebido no período (todos os CC). */
  pct?:       number;
}

export interface ContaStat {
  accountId:  string;
  name:       string;
  valor:      number;
  quantidade: number;
  items:      OutcomeItem[];
  /** % do total de despesas no período (todos os planos, não só o Top exibido). */
  pct?:       number;
}

export interface CredorStat {
  name:       string;
  valor:      number;
  quantidade: number;
  items:      OutcomeItem[];
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const MES_LABELS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
                    "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function getMonthLabel(dateStr: string): string {
  if (!dateStr || dateStr.length < 7) return "—";
  const [yearStr, monthStr] = dateStr.substring(0, 7).split("-");
  const year  = (yearStr ?? "????").slice(2);
  const month = parseInt(monthStr ?? "0", 10) - 1;
  return `${MES_LABELS[month] ?? "??"}/${year}`;
}

function getMonthKey(dateStr: string): string {
  return dateStr?.substring(0, 7) ?? "";
}

function getEffectiveDate(item: { dueDate: string; issueDate: string }): string {
  return item.dueDate || item.issueDate;
}

/** Data para agrupar no fluxo: pagamento se quitado, senão vencimento/emissão. */
function cashflowDateOutcome(o: OutcomeItem): string {
  if (o.isPaid && o.paymentDate && o.paymentDate.length >= 10) return o.paymentDate.substring(0, 10);
  const d = getEffectiveDate(o);
  return d.length >= 10 ? d.substring(0, 10) : "";
}

function cashflowDateIncome(i: IncomeItem): string {
  if (i.isPaid && i.paymentDate && i.paymentDate.length >= 10) return i.paymentDate.substring(0, 10);
  const d = getEffectiveDate(i);
  return d.length >= 10 ? d.substring(0, 10) : "";
}

/* ─── Computações ────────────────────────────────────────────────────────── */

/**
 * Fluxo mensal: despesas só parcelas **pagas**, receitas só **recebidas**,
 * mais extrato SEMOG (crédito/débito por data do movimento).
 */
export function computeFluxoMensal(
  outcome: OutcomeItem[],
  income: IncomeItem[],
  statements: BankStatementMovement[] = []
): MesStat[] {
  const map = new Map<string, MesStat>();

  for (const item of outcome) {
    if (!item.isPaid) continue;
    const date = cashflowDateOutcome(item);
    const key  = getMonthKey(date);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, {
        mes: getMonthLabel(date),
        mesKey: key,
        receitas: 0,
        despesas: 0,
        saldo: 0,
        outcomeItems: [],
        incomeItems: [],
        statementItems: [],
      });
    }
    const m = map.get(key)!;
    m.despesas += outcomeParcelValorExibido(item);
    m.outcomeItems.push(item);
  }

  for (const item of income) {
    if (!item.isPaid) continue;
    const date = cashflowDateIncome(item);
    const key  = getMonthKey(date);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, {
        mes: getMonthLabel(date),
        mesKey: key,
        receitas: 0,
        despesas: 0,
        saldo: 0,
        outcomeItems: [],
        incomeItems: [],
        statementItems: [],
      });
    }
    const m = map.get(key)!;
    m.receitas += incomeReceiptLiquido(item);
    m.incomeItems.push(item);
  }

  for (const st of statements) {
    const key = getMonthKey(st.movementDate);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, {
        mes: getMonthLabel(st.movementDate),
        mesKey: key,
        receitas: 0,
        despesas: 0,
        saldo: 0,
        outcomeItems: [],
        incomeItems: [],
        statementItems: [],
      });
    }
    const m = map.get(key)!;
    m.receitas += st.credit;
    m.despesas += st.debit;
    m.statementItems.push(st);
  }

  for (const m of map.values()) m.saldo = m.receitas - m.despesas;

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => v);
}

export function computeSaldoAcumulado(meses: MesStat[]): SaldoAcumuladoStat[] {
  let acum = 0;
  return meses.map((m) => {
    acum += m.saldo;
    return {
      mes:       m.mes,
      mesKey:    m.mesKey,
      receitas:  m.receitas,
      despesas:  m.despesas,
      saldo:     m.saldo,
      saldoAcum: acum,
    };
  });
}

export function computeStatusPagamentos(outcome: OutcomeItem[]): StatusPagStat[] {
  const pagos   = outcome.filter((o) => o.isPaid);
  const abertos = outcome.filter((o) => !o.isPaid);
  const result: StatusPagStat[] = [];
  if (pagos.length > 0) {
    result.push({
      name:       "Pago",
      value:      pagos.reduce((s, o) => s + outcomeParcelValorExibido(o), 0),
      quantidade: pagos.length,
      fill:       "#10b981",
      items:      pagos,
    });
  }
  if (abertos.length > 0) {
    result.push({
      name:       "Em Aberto",
      value:      abertos.reduce((s, o) => s + outcomeParcelValorExibido(o), 0),
      quantidade: abertos.length,
      fill:       "#f59e0b",
      items:      abertos,
    });
  }
  return result;
}

/**
 * Total pago por centro de custo no período (parcelas com data de pagamento no intervalo).
 * Alinha ao escopo do card «Pagamentos» e do modal de parcelas pagas.
 */
export function computePagamentosPorCentroDeCusto(
  outcome: OutcomeItem[],
  startDate: string,
  endDate: string,
  ccNameMap?: Map<string, string>
): CentroCustoFinStat[] {
  const paidInPeriod = outcome.filter((o) => {
    if (!o.isPaid) return false;
    const d = (o.paymentDate || "").substring(0, 10);
    if (d.length < 10) return false;
    return d >= startDate && d <= endDate;
  });

  const map = new Map<string, CentroCustoFinStat>();
  for (const item of paidInPeriod) {
    const resolvedName =
      item.costCenterName ||
      (ccNameMap && item.costCenterId ? ccNameMap.get(item.costCenterId) : undefined) ||
      item.costCenterId ||
      "Sem Centro de Custo";
    const key = item.costCenterId || resolvedName;
    if (!map.has(key)) {
      map.set(key, { name: resolvedName, valor: 0, quantidade: 0, items: [] });
    }
    const s = map.get(key)!;
    s.valor += outcomeParcelValorExibido(item);
    s.quantidade += 1;
    s.items.push(item);
  }
  const all = Array.from(map.values()).sort((a, b) => b.valor - a.valor);
  const grandTotal = all.reduce((s, r) => s + r.valor, 0);
  return all.map((r) => ({
    ...r,
    pct: grandTotal > 0 ? (r.valor / grandTotal) * 100 : 0,
  }));
}

/**
 * Total recebido por centro de custo no período (títulos com data de recebimento no intervalo).
 * Alinha ao escopo do card «Recebimentos» (parcelas income) e do modal de títulos recebidos.
 */
export function computeRecebimentosPorCentroDeCusto(
  income: IncomeItem[],
  startDate: string,
  endDate: string,
  ccNameMap?: Map<string, string>
): CentroCustoRecebimentoStat[] {
  const receivedInPeriod = income.filter((i) => {
    if (!i.isPaid) return false;
    const d = (i.paymentDate || "").substring(0, 10);
    if (d.length < 10) return false;
    return d >= startDate && d <= endDate;
  });

  const map = new Map<string, CentroCustoRecebimentoStat>();
  for (const item of receivedInPeriod) {
    const resolvedName =
      item.costCenterName ||
      (ccNameMap && item.costCenterId ? ccNameMap.get(item.costCenterId) : undefined) ||
      item.costCenterId ||
      "Sem Centro de Custo";
    const key = item.costCenterId || resolvedName;
    if (!map.has(key)) {
      map.set(key, { name: resolvedName, valor: 0, quantidade: 0, items: [] });
    }
    const s = map.get(key)!;
    s.valor += incomeReceiptLiquido(item);
    s.quantidade += 1;
    s.items.push(item);
  }
  const all = Array.from(map.values()).sort((a, b) => b.valor - a.valor);
  const grandTotal = all.reduce((s, r) => s + r.valor, 0);
  return all.map((r) => ({
    ...r,
    pct: grandTotal > 0 ? (r.valor / grandTotal) * 100 : 0,
  }));
}

export function computeDespesasPorCentro(
  outcome:   OutcomeItem[],
  ccNameMap?: Map<string, string>
): CentroCustoFinStat[] {
  const map = new Map<string, CentroCustoFinStat>();
  for (const item of outcome) {
    /* Resolve o nome: prefere o que vier da API, depois o mapa de opções, depois o ID */
    const resolvedName =
      item.costCenterName ||
      (ccNameMap && item.costCenterId ? ccNameMap.get(item.costCenterId) : undefined) ||
      item.costCenterId ||
      "Sem Centro de Custo";

    /* Usa o ID como chave do mapa para não fundir CCs distintos com mesmo nome */
    const key = item.costCenterId || resolvedName;

    if (!map.has(key)) {
      map.set(key, { name: resolvedName, valor: 0, quantidade: 0, items: [] });
    }
    const s = map.get(key)!;
    s.valor      += outcomeParcelValorExibido(item);
    s.quantidade += 1;
    s.items.push(item);
  }
  return Array.from(map.values())
    .sort((a, b) => b.valor - a.valor)
    .slice(0, 10);
}

export function computeDespesasPorConta(outcome: OutcomeItem[]): ContaStat[] {
  const map = new Map<string, ContaStat>();
  for (const item of outcome) {
    /* Usa o nome da conta; fallback para o ID; fallback para tipo de documento */
    const resolvedName =
      item.accountName ||
      (item.accountId ? `Conta ${item.accountId}` : "") ||
      item.documentType ||
      "Sem Classificação";
    const resolvedId = item.accountId || resolvedName;

    if (!map.has(resolvedId)) {
      map.set(resolvedId, { accountId: resolvedId, name: resolvedName, valor: 0, quantidade: 0, items: [] });
    }
    const s = map.get(resolvedId)!;
    s.valor      += outcomeParcelValorExibido(item);
    s.quantidade += 1;
    s.items.push(item);
  }
  const all = Array.from(map.values()).sort((a, b) => b.valor - a.valor);
  const grandTotal = all.reduce((s, r) => s + r.valor, 0);
  return all.map((r) => ({
    ...r,
    pct: grandTotal > 0 ? (r.valor / grandTotal) * 100 : 0,
  }));
}

export function computeRankingCredores(outcome: OutcomeItem[]): CredorStat[] {
  const map = new Map<string, CredorStat>();
  for (const item of outcome) {
    const key = item.creditorName || "Sem Nome";
    if (!map.has(key)) map.set(key, { name: key, valor: 0, quantidade: 0, items: [] });
    const s = map.get(key)!;
    s.valor      += outcomeParcelValorExibido(item);
    s.quantidade += 1;
    s.items.push(item);
  }
  return Array.from(map.values()).sort((a, b) => b.valor - a.valor);
}
